import { useState, useRef, useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

const ROLE_LABELS = { admin: 'Admin', user: 'User', viewer: 'Viewer' }

// Roles available to switch to, keyed by the user's actual app_role
const SWITCH_OPTIONS = {
  admin:  ['admin', 'user', 'viewer'],
  user:   ['user', 'viewer'],
  viewer: [],
}

export default function Layout({ children, footerContent }) {
  const { user, logout, effectiveRole, switchRole } = useAuth()
  const [menuOpen, setMenuOpen]           = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const menuRef = useRef(null)
  const location = useLocation()

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    if (!menuOpen) return
    const handleMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [menuOpen])

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileNavOpen])

  const actualRole    = user?.app_role
  const switchOptions = SWITCH_OPTIONS[actualRole] ?? []
  const isSwitched    = effectiveRole && effectiveRole !== actualRole

  const isProd = import.meta.env.VITE_APP_ENV === 'prod'

  return (
    <div className="app-shell">
      <header className={isProd ? 'app-header' : 'app-header app-header--dev'}>
        <div className="app-header-inner">
          <Link to="/experiments" className="app-header-brand">
            <img src="/logo.png" alt="AI Pulse logo" />
            <h1>AI Pulse</h1>
          </Link>

          {/* Desktop nav */}
          <nav className="app-header-nav" aria-label="Main navigation">
            <NavLink to="/experiments" className={({ isActive }) => isActive ? 'active' : ''}>
              AI Briefs
            </NavLink>
            <NavLink to="/results" className={({ isActive }) => isActive ? 'active' : ''}>
              Results
            </NavLink>
            <NavLink to="/analytics" className={({ isActive }) => isActive ? 'active' : ''}>
              Analytics
            </NavLink>
            {effectiveRole === 'admin' && (
              <NavLink to="/admin/users" className={({ isActive }) => isActive ? 'active' : ''}>
                Admin
              </NavLink>
            )}

            {/* ── User menu ─────────────────────────────── */}
            <div className="user-menu" ref={menuRef}>
              <button
                type="button"
                className="app-nav-btn user-menu-btn"
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                {user?.display_name ?? 'Account'}
                <span className="role-badge">{ROLE_LABELS[effectiveRole]}</span>
                <span className="user-menu-caret" aria-hidden="true">▾</span>
              </button>

              {menuOpen && (
                <div className="user-menu-dropdown" role="menu">
                  <div className="user-menu-info">
                    <div className="user-menu-info-row">
                      <span className="user-menu-info-label">Role</span>
                      <span className="user-menu-info-value">
                        {ROLE_LABELS[actualRole] ?? actualRole}
                      </span>
                    </div>
                    {isSwitched && (
                      <div className="user-menu-info-sub">
                        Viewing as: {ROLE_LABELS[effectiveRole]}
                      </div>
                    )}
                  </div>

                  {switchOptions.length > 1 && (
                    <>
                      <div className="user-menu-separator" />
                      <div className="user-menu-section-label">Switch role</div>
                      {switchOptions.map((role) => (
                        <button
                          key={role}
                          type="button"
                          className={`user-menu-item${effectiveRole === role ? ' active' : ''}`}
                          role="menuitem"
                          onClick={() => { switchRole(role); setMenuOpen(false) }}
                        >
                          <span className="user-menu-check">
                            {effectiveRole === role ? '✓' : ''}
                          </span>
                          {ROLE_LABELS[role]}
                        </button>
                      ))}
                    </>
                  )}

                  <div className="user-menu-separator" />
                  <button
                    type="button"
                    className="user-menu-item user-menu-logout"
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); logout() }}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Hamburger button — mobile only */}
          <button
            type="button"
            className="hamburger-btn"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>
      </header>

      {/* Mobile nav overlay + drawer */}
      {mobileNavOpen && (
        <>
          <div
            className="mobile-nav-overlay"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <nav className="mobile-nav-drawer" aria-label="Mobile navigation">
            <div className="mobile-nav-header">
              <span className="mobile-nav-title">AI Pulse</span>
              <button
                type="button"
                className="mobile-nav-close"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation"
              >
                ×
              </button>
            </div>

            <div className="mobile-nav-links">
              <NavLink to="/experiments" className={({ isActive }) => isActive ? 'active' : ''}>
                AI Briefs
              </NavLink>
              <NavLink to="/results" className={({ isActive }) => isActive ? 'active' : ''}>
                Results
              </NavLink>
              <NavLink to="/analytics" className={({ isActive }) => isActive ? 'active' : ''}>
                Analytics
              </NavLink>
              {effectiveRole === 'admin' && (
                <NavLink to="/admin/users" className={({ isActive }) => isActive ? 'active' : ''}>
                  Admin
                </NavLink>
              )}

              {switchOptions.length > 1 && (
                <>
                  <div className="user-menu-separator" style={{ margin: '0.5rem 0' }} />
                  <div className="user-menu-section-label" style={{ padding: '0.25rem 1.25rem 0.125rem', color: 'rgba(255,255,255,0.4)' }}>
                    Switch role
                  </div>
                  {switchOptions.map((role) => (
                    <button
                      key={role}
                      type="button"
                      className="mobile-nav-logout"
                      style={{ color: effectiveRole === role ? 'white' : undefined, fontWeight: effectiveRole === role ? 600 : undefined }}
                      onClick={() => { switchRole(role); setMobileNavOpen(false) }}
                    >
                      {effectiveRole === role ? '✓ ' : ''}{ROLE_LABELS[role]}
                    </button>
                  ))}
                </>
              )}
            </div>

            <div className="mobile-nav-footer">
              <div className="mobile-nav-user">
                {user?.display_name} · {ROLE_LABELS[effectiveRole] ?? effectiveRole}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ width: '100%' }}
                onClick={() => { setMobileNavOpen(false); logout() }}
              >
                Log out
              </button>
            </div>
          </nav>
        </>
      )}

      <main className="app-main">
        <div className="app-content">
          {children}
        </div>
      </main>

      {footerContent ? (
        <footer className="app-footer">{footerContent}</footer>
      ) : (
        <footer className="app-footer">
          © {new Date().getFullYear()} Advance Local — AI Pulse
        </footer>
      )}
    </div>
  )
}
